// check if the current page is in an iframe
export const isInIframe = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return false;
    }
}

// Border-radius embed params arrive from the URL as strings. A bare "8" — the format our
// public API reference documented — reached emotion as `border-radius: 8`, which is invalid
// CSS: the browser dropped it and the theme fallback never fired (the value wasn't empty),
// so cards, buttons and inputs all rendered at 0px. That is worse than passing nothing.
// Parsing to a number accepts both "8" and "8px", and a number is what emotion needs: it
// appends "px" to numeric style values but passes strings through untouched, so "8px" is
// also safe while a bare "8" is not.
//
// NOTE: this does NOT keep theme/overrides/Card.jsx and Dialog.jsx (which compute
// Number(theme.shape.borderRadius) * 2) working. IFrameView rebuilds the theme with an
// explicit `components` key that replaces the whole override map, so in embed mode those
// two never run at all — cards and dialogs fall back to MUI's own shape-derived styling.
// That is a separate, pre-existing bug; see the tracking issue.
//
// Anything else (other units, junk) returns undefined so the caller falls back to the theme.
const RADIUS_PARAM_PATTERN = /^(\d+(?:\.\d+)?)(?:px)?$/i;

export const parseRadiusParam = (value) => {
    if (value === undefined || value === null) return undefined;
    const match = RADIUS_PARAM_PATTERN.exec(String(value).trim());
    if (!match) return undefined;
    // \d+ accepts arbitrarily long digit strings, which parseFloat turns into Infinity
    // (and MUI then emits an invalid "Infinitypx"). Treat that as junk like any other.
    const radius = parseFloat(match[1]);
    return Number.isFinite(radius) ? radius : undefined;
}

// Cache the result to avoid re-parsing on every call
let embedModeCache = null;

// check if the current page should be in embed mode (iframe or embed parameter)
export const isEmbedMode = () => {
    // If we already checked, return cached result
    if (embedModeCache !== null) {
        return embedModeCache;
    }

    // Check if in iframe first (fastest check)
    if (isInIframe()) {
        embedModeCache = true;
        return true;
    }

    // Check for embed parameter in URL
    try {
        // Use window.location.search directly for immediate access
        if (typeof window !== 'undefined' && window.location && window.location.search) {
            // Quick string check first for better performance
            if (window.location.search.includes('embed=true')) {
                // Verify with URLSearchParams for accuracy
                const urlParams = new URLSearchParams(window.location.search);
                embedModeCache = urlParams.get('embed') === 'true';
                return embedModeCache;
            }
        }
        embedModeCache = false;
        return false;
    } catch (e) {
        embedModeCache = false;
        return false;
    }
}